import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Snowflake,
  Zap,
  Percent,
  X,
  Clock,
  Copy,
  Check
} from 'lucide-react';
import GemIcon from '@/components/icons/GemIcon';
import { cn } from '@/utils/cn';
import { userServiceApi, paymentServiceApi } from '@/services/api';
import { getValidAccessToken } from '@/services/authService';
import { writeClipboardText } from '@/utils/clipboard';
import { analyticsService } from '@/services/analyticsService';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import FeatureGuideOverlay from '@/components/ui/FeatureGuideOverlay';

// --- Types ---
interface GemPlan {
  plan_id: number;
  plan_name: string;
  gems_amount: number;
  price: string;
  discount_price: string | null;
}

interface GemShopItem {
  item_id: number;
  name: string;
  description: string;
  icon: string | null;
  action: string;
  gems_price: number;
  gems_needed: number;
  progress: number;
  is_active: boolean;
  remaining_hours: number | string | null;
  remaining_minutes: number | string | null;
  discount_code?: string;
}

interface GemShopResponse {
  user_gems: number;
  items: GemShopItem[];
}

interface PurchaseResponse {
  success: boolean;
  message: string;
  detail?: string;
  data?: {
    item_name?: string;
    current_gems?: number;
    required_gems?: number;
    item_price?: number;
    duration_hours?: number;
    expires_at?: string;
    remaining_hours?: number;
    remaining_minutes?: number;
    discount_code?: string;
  };
}

interface BottomSheetState {
  isOpen: boolean;
  type: 'success' | 'error' | 'warning';
  message: string;
  action?: string;
  data?: PurchaseResponse['data'];
}

const DISCOUNT_ACTIONS = new Set(['subscription_discount', 'gems_discount']);

const isDiscountAction = (action: string) => DISCOUNT_ACTIONS.has(action);

const GEMS_PAGE_GUIDE_STEPS = [
  {
    id: 'gems-page-overview',
    title: 'الماس و قابلیت‌های ویژه',
    description: 'اینجا می‌تونی الماس بخری و از قابلیت‌های ویژه مثل تخفیف، حفظ پیوستگی و رشد چندبرابری امتیاز استفاده کنی.',
  },
];

// Icon mapping based on action type
const getIconForAction = (action: string): React.ElementType => {
  switch (action) {
    case 'streak_freeze':
      return Snowflake;
    case 'xp_boost':
      return Zap;
    case 'subscription_discount':
    case 'gems_discount':
      return Percent;
    default:
      return Zap;
  }
};

// Icon styling based on action type
const getIconStyle = (action: string): { bg: string; color: string } => {
  switch (action) {
    case 'streak_freeze':
      return { bg: 'bg-cyan-50', color: 'text-cyan-600' };
    case 'xp_boost':
      return { bg: 'bg-amber-50', color: 'text-amber-600' };
    case 'subscription_discount':
    case 'gems_discount':
      return { bg: 'bg-pink-50', color: 'text-pink-600' };
    default:
      return { bg: 'bg-indigo-50', color: 'text-indigo-600' };
  }
};

const isImageIcon = (icon: string | null | undefined): boolean => {
  return !!icon && (/^https?:\/\//i.test(icon) || icon.startsWith('/uploads/') || icon.startsWith('uploads/'));
};

const GemItemIcon: React.FC<{
  icon: string | null;
  action: string;
  className?: string;
}> = ({ icon, action, className }) => {
  const [imageError, setImageError] = useState(false);
  const IconComponent = getIconForAction(action);

  useEffect(() => setImageError(false), [icon]);

  if (isImageIcon(icon) && !imageError) {
    return <img src={icon!} alt="" className={cn('object-contain', className)} onError={() => setImageError(true)} />;
  }

  return <IconComponent className={className} strokeWidth={2} />;
};

const getProgressBarStyle = (action: string): string => {
  switch (action) {
    case 'streak_freeze':
      return 'bg-cyan-500';
    case 'xp_boost':
      return 'bg-amber-400';
    case 'subscription_discount':
    case 'gems_discount':
      return 'bg-pink-400';
    default:
      return 'bg-primary-400';
  }
};

const getActionButtonStyle = (action: string): string => {
  switch (action) {
    case 'streak_freeze':
      return 'bg-cyan-500 hover:bg-cyan-600';
    case 'xp_boost':
      return 'bg-amber-400 hover:bg-amber-500';
    case 'subscription_discount':
    case 'gems_discount':
      return 'bg-pink-400 hover:bg-pink-500';
    default:
      return 'bg-primary-500 hover:bg-primary-600';
  }
};

// --- Utils ---
const toPersianDigits = (num: number | string) =>
  String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const formatPrice = (price: string | number) => {
  const num = typeof price === 'string' ? parseFloat(price) : price;
  return toPersianDigits(Math.round(num).toLocaleString());
};

// Convert Gregorian date to Persian (Shamsi)
const toPersianDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      calendar: 'persian'
    };
    return new Intl.DateTimeFormat('fa-IR', options).format(date);
  } catch {
    return dateStr;
  }
};

const copyToClipboard = async (text: string) => {
  await writeClipboardText(text);
};

// Fetch gem plans from payment service
const getGemPlans = async (): Promise<GemPlan[]> => {
  const response = await paymentServiceApi.get<GemPlan[]>('/gems-plans');
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to fetch gem plans');
};

// Fetch gem shop items
const getGemShopItems = async (): Promise<GemShopResponse> => {
  const response = await userServiceApi.get<GemShopResponse>('/gem-shop/items');
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to fetch gem shop items');
};

// Purchase item
const purchaseItem = async (itemId: number): Promise<PurchaseResponse> => {
  const response = await userServiceApi.post<PurchaseResponse>(`/gem-shop/purchase/${itemId}`);
  if (response.success && response.data) {
    return response.data;
  }
  if (response.data) {
    return response.data;
  }
  return {
    success: false,
    message: response.error || response.detail || 'خطا در خرید آیتم',
    detail: response.detail
  };
};

let hasShownAnimation = false;

// --- Bottom Sheet Component ---
const ResultBottomSheet: React.FC<{
  sheet: BottomSheetState;
  onClose: () => void;
}> = ({ sheet, onClose }) => {
  const { isOpen, type, message, action, data } = sheet;
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const IconComponent = action ? getIconForAction(action) : null;
  const iconStyle = action ? getIconStyle(action) : null;

  useEffect(() => {
    if (!isOpen) {
      setCopiedCode(null);
    }
  }, [isOpen]);

  const handleCopyDiscountCode = async (discountCode: string) => {
    try {
      await copyToClipboard(discountCode);
      setCopiedCode(discountCode);
      window.setTimeout(() => {
        setCopiedCode((current) => (current === discountCode ? null : current));
      }, 1800);
    } catch (error) {
      console.warn('Failed to copy discount code', error);
    }
  };

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Bottom Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl z-50 max-h-[80vh] overflow-y-auto"
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 left-4 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>

            {/* Content */}
            <div className="p-5 pb-8" dir="rtl">
              {/* Item Icon */}
              {IconComponent && iconStyle && (
                <div className="flex justify-center mb-4">
                  <div className={cn("flex size-16 items-center justify-center rounded-2xl", iconStyle.bg, iconStyle.color)}>
                    <IconComponent className="w-8 h-8" strokeWidth={2} />
                  </div>
                </div>
              )}

              {/* Message */}
              <p className="text-base font-bold text-gray-800 text-center mb-5 leading-relaxed px-2">
                {message}
              </p>

              {/* Data details - Only for success: duration_hours & expires_at */}
              {type === 'success' && data && (
                <div className="bg-gray-50 rounded-2xl p-4 space-y-3 mb-5">
                  {data.discount_code && isDiscountAction(action || '') && (
                    <div className="rounded-2xl bg-gradient-to-l from-primary-500 via-primary-400 to-pink-500 p-[1px] shadow-lg shadow-primary-100/80">
                      <div className="rounded-2xl bg-white px-4 py-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-primary-600 mb-1">
                              کد تخفیف شما
                            </p>
                            <div className="flex items-center gap-2">
                              <span className="rounded-xl bg-primary-50 px-3 py-2 text-lg font-black tracking-[0.32em] text-primary-700">
                                {data.discount_code}
                              </span>
                            </div>
                          </div>

                          <button
                            onClick={() => handleCopyDiscountCode(data.discount_code!)}
                            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-primary-500 text-white shadow-md transition-all hover:bg-primary-600 active:scale-95"
                            aria-label="کپی کد تخفیف"
                          >
                            {copiedCode === data.discount_code ? (
                              <Check className="h-5 w-5" />
                            ) : (
                              <Copy className="h-5 w-5" />
                            )}
                          </button>
                        </div>

                        <p className="mt-3 text-xs font-medium text-gray-500">
                          این کد را کپی کن و موقع خرید وارد کن.
                        </p>
                      </div>
                    </div>
                  )}

                  {data.duration_hours !== undefined && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-500">مدت زمان:</span>
                      <span className="font-bold text-gray-800">
                        {toPersianDigits(data.duration_hours)} ساعت
                      </span>
                    </div>
                  )}

                  {data.expires_at && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-500">تاریخ انقضا:</span>
                      <span className="font-bold text-gray-800">
                        {toPersianDate(data.expires_at)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Error/Warning data */}
              {type !== 'success' && data && (
                <div className="bg-gray-50 rounded-2xl p-4 space-y-3 mb-5">
                  {data.required_gems !== undefined && (
                    <div className="flex items-center gap-2 text-sm">
                      <GemIcon size={16} />
                      <span className="text-gray-500">جم مورد نیاز:</span>
                      <span className="font-bold text-red-600">
                        {toPersianDigits(data.required_gems)}
                      </span>
                    </div>
                  )}

                  {(data.remaining_hours !== undefined || data.remaining_minutes !== undefined) && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-gray-500">زمان باقی‌مانده:</span>
                      <span className="font-bold text-amber-600">
                        {data.remaining_hours !== undefined && data.remaining_hours > 0 && `${toPersianDigits(data.remaining_hours)} ساعت `}
                        {data.remaining_minutes !== undefined && data.remaining_minutes > 0 && `${toPersianDigits(data.remaining_minutes)} دقیقه`}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Close button - Always primary theme color */}
              <button
                onClick={onClose}
                className="w-full py-3.5 rounded-xl font-bold text-white bg-primary-500 hover:bg-primary-600 transition-all active:scale-95"
              >
                متوجه شدم
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// --- Main Component ---
const GemsPage = () => {
  const { user } = useAuth();
  useEffect(() => {
    localStorage.setItem('dashboard_gems_tooltip_seen', 'true');
  }, []);

  const navigate = useNavigate();
  const [balance, setBalance] = useState<number>(0);
  const [gemPlans, setGemPlans] = useState<GemPlan[]>([]);
  const [shopItems, setShopItems] = useState<GemShopItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState<number | null>(null);
  const [bottomSheet, setBottomSheet] = useState<BottomSheetState>({
    isOpen: false,
    type: 'success',
    message: ''
  });
  const [copiedItemCode, setCopiedItemCode] = useState<string | null>(null);

  const [shouldAnimate] = useState(!hasShownAnimation);

  const fetchData = async () => {
    try {
      const token = await getValidAccessToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Fetch all data in parallel
      const [shopData, plansData] = await Promise.all([
        getGemShopItems().catch(e => {
          console.warn('⚠️ [GemsPage] Failed to load shop items', e);
          return null;
        }),
        getGemPlans().catch(e => {
          console.warn('⚠️ [GemsPage] Failed to load gem plans', e);
          return [];
        })
      ]);

      if (shopData) {
        setBalance(shopData.user_gems);
        setShopItems(shopData.items);
      }
      setGemPlans(plansData);
    } catch (e) {
      console.warn('⚠️ [GemsPage] Failed to load data', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    hasShownAnimation = true;
    fetchData();
  }, []);

  useEffect(() => {
    if (!isLoading && gemPlans.length > 0) {
      void analyticsService.logEvent('view_gems_plans', {
        plan_count: gemPlans.length,
        payment_type: 'gems',
      });
    }
  }, [isLoading, gemPlans.length]);

  const handlePurchase = async (item: GemShopItem) => {
    setIsPurchasing(item.item_id);
    try {
      const result = await purchaseItem(item.item_id);

      if (result.success) {
        setBottomSheet({
          isOpen: true,
          type: 'success',
          message: result.message,
          action: item.action,
          data: result.data
        });
        await fetchData();
      } else {
        setBottomSheet({
          isOpen: true,
          type: result.data?.remaining_hours !== undefined || result.data?.remaining_minutes !== undefined ? 'warning' : 'error',
          message: result.message || result.detail || 'خطا در خرید آیتم',
          action: item.action,
          data: result.data
        });
      }
    } catch (e: any) {
      setBottomSheet({
        isOpen: true,
        type: 'error',
        message: e.message || 'خطا در خرید آیتم',
        action: item.action
      });
    } finally {
      setIsPurchasing(null);
    }
  };

  const closeBottomSheet = () => {
    setBottomSheet(prev => ({ ...prev, isOpen: false }));
  };

  const handleCopyItemDiscountCode = async (discountCode: string) => {
    try {
      await copyToClipboard(discountCode);
      setCopiedItemCode(discountCode);
      window.setTimeout(() => {
        setCopiedItemCode((current) => (current === discountCode ? null : current));
      }, 1800);
    } catch (error) {
      console.warn('Failed to copy item discount code', error);
    }
  };

  // Handle gem package purchase - navigate to payment confirmation
  const handleGemPurchase = (plan: GemPlan) => {
    void analyticsService.logEvent('select_gems_plan', {
      plan_id: String(plan.plan_id),
      plan_name: plan.plan_name,
      payment_type: 'gems',
      gems_amount: plan.gems_amount,
    });

    navigate('/payment-confirmation', {
      state: {
        planId: plan.plan_id,
        paymentType: 'gems',
        planData: {
          plan_name: plan.plan_name,
          gems_amount: plan.gems_amount,
          price: plan.price,
          discount_price: plan.discount_price
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] font-persian pb-8" dir="rtl">
      {/* Result Bottom Sheet */}
      <ResultBottomSheet sheet={bottomSheet} onClose={closeBottomSheet} />

      {!isLoading && user?.is_registered && !user.is_guest && (
        <FeatureGuideOverlay
          steps={GEMS_PAGE_GUIDE_STEPS}
        />
      )}

      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-gray-100 bg-white px-4 py-3 pt-safe-top">
        <div className="relative mt-2 flex h-9 w-full items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="z-10 flex h-9 w-9 items-center justify-center rounded-full p-1.5 transition-all duration-200 hover:scale-110 hover:bg-gray-100"
          aria-label="بازگشت"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>

        <h1 className="absolute left-1/2 top-[2px] z-0 flex h-9 -translate-x-1/2 items-center justify-center whitespace-nowrap text-base font-bold leading-none text-foreground">فروشگاه</h1>

        <div className="z-10 inline-flex h-9 items-center gap-1 rounded-2xl border border-gray-100 bg-white px-2 text-white shadow-[0_0_8px_-2px_rgba(0,0,0,0.12)]">
          <span className="flex size-5 items-center justify-center">
            <GemIcon size={18} color="flatCyan" />
          </span>
          <p className="relative top-[1.5px] whitespace-nowrap text-center text-[13px] font-black leading-none text-gray-800 sm:text-sm">{toPersianDigits(balance.toLocaleString())} الماس</p>
        </div>
        </div>
      </div>

      <div className="flex flex-col gap-6 p-4 max-w-lg mx-auto overflow-hidden">

        {/* Gem Packages Section */}
        <section>
          <h2 className="font-persian font-bold text-base mb-3 text-right px-1">بسته‌های الماس</h2>

          {isLoading ? (
            <div className="flex flex-col gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="flex h-[84px] items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_0_10px_-2px_rgba(0,0,0,0.14)]"
                >
                  <div className="size-11 shrink-0 animate-pulse rounded-xl bg-cyan-50" />

                  <div className="min-w-0 flex-1 animate-pulse space-y-2">
                    <div className="h-4 w-24 rounded bg-gray-200" />
                    <div className="h-3 w-32 rounded bg-gray-100" />
                  </div>

                  <div className="h-9 w-24 shrink-0 animate-pulse rounded-xl bg-cyan-100" />
                </div>
              ))}
            </div>
          ) : gemPlans.length > 0 ? (
            <div className="flex flex-col gap-3">
              {gemPlans.map((plan, index) => {
                const hasDiscount = plan.discount_price !== null;
                const finalPrice = Number(hasDiscount ? plan.discount_price : plan.price);
                const originalPrice = Number(plan.price);
                const discountPercent = hasDiscount && originalPrice > 0
                  ? Math.round((1 - (finalPrice / originalPrice)) * 100)
                  : 0;
                const pricePerGem = plan.gems_amount > 0
                  ? Math.round(finalPrice / plan.gems_amount)
                  : 0;

                return (
                  <motion.div
                    key={plan.plan_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 }}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleGemPurchase(plan)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleGemPurchase(plan);
                      }
                    }}
                    className="relative flex cursor-pointer items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_0_10px_-2px_rgba(0,0,0,0.14)] transition-shadow hover:shadow-[0_0_12px_-2px_rgba(0,0,0,0.2)]"
                  >
                    {/* Discount badge - top left in RTL */}
                    {hasDiscount && discountPercent > 0 && (
                      <div className="absolute -top-2 left-3 rounded-lg bg-red-500 px-2 py-1 text-[9px] font-bold leading-none text-white shadow-sm">
                        <bdi dir="rtl" className="inline-block">
                          {toPersianDigits(discountPercent)} %
                        </bdi>{' '}تخفیف
                      </div>
                    )}

                    {/* RTL order: icon and package information on the right */}
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-cyan-50">
                        <GemIcon size={25} color="flatCyan" />
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm leading-tight text-gray-800">
                          <span className="font-semibold">{toPersianDigits(plan.gems_amount.toLocaleString())}</span>{' '}
                          <span className="text-[12px] font-normal">الماس</span>
                        </p>
                        <p className="mt-1 text-[10px] font-medium leading-relaxed text-gray-500">
                          <span>هر الماس </span>
                          <span className="font-semibold text-cyan-600">{formatPrice(pricePerGem)} تومان</span>
                        </p>
                      </div>
                    </div>

                    {/* Final price button at the end of the RTL row */}
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        handleGemPurchase(plan);
                      }}
                      className="mr-auto shrink-0 rounded-xl bg-cyan-500 px-3 py-2 text-[11px] font-bold text-white shadow-md shadow-cyan-500/30 transition-all hover:bg-cyan-600 active:scale-95"
                    >
                      {formatPrice(finalPrice)} تومان
                    </button>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-8 text-sm">
              بسته‌ای موجود نیست
            </div>
          )}
        </section>

        {/* Special Features Section */}
        <section>
          <h2 className="font-persian font-bold text-base mb-3 text-right px-1">قابلیت‌های ویژه</h2>

          {isLoading ? (
            <div className="flex flex-col gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 rounded-2xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {shopItems.map((item, index) => {
                const iconStyle = getIconStyle(item.action);

                const isActive = item.is_active;
                const canUse = item.gems_needed === 0 && item.progress === 100;
                const isDisabled = isActive || !canUse;
                const isThisPurchasing = isPurchasing === item.item_id;

                const gemsHave = item.gems_price - item.gems_needed;
                const isProgressFull = item.progress >= 100;
                const showDiscountCode = isDiscountAction(item.action) && item.is_active && !!item.discount_code;
                const progressBarStyle = getProgressBarStyle(item.action);
                const actionButtonStyle = getActionButtonStyle(item.action);

                return (
                  <motion.div
                    key={item.item_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + (index * 0.08) }}
                    className={cn(
                      "flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-[0_0_10px_-2px_rgba(0,0,0,0.14)] border transition-shadow",
                      isActive ? "border-green-200 bg-green-50/20" : "border-gray-100 hover:shadow-md"
                    )}
                  >
                    {/* Header */}
                    <div className="flex flex-col gap-2">
                      {/* Action button */}
                      <div className="flex justify-end">
                        {isActive ? (
                          <div className="flex h-8 min-w-[90px] items-center justify-center rounded-xl bg-green-100 px-2.5 text-[10px] font-bold text-green-700">
                            <span>
                              {item.remaining_hours === '∞' || item.remaining_minutes === '∞' ? (
                                'فعال تا ∞'
                              ) : (
                                <>
                                  فعال تا {item.remaining_hours !== null && Number(item.remaining_hours) > 0 && `${toPersianDigits(Number(item.remaining_hours))} ساعت`}
                                  {item.remaining_hours !== null && Number(item.remaining_hours) > 0 && item.remaining_minutes !== null && Number(item.remaining_minutes) > 0 && ' و '}
                                  {item.remaining_minutes !== null && Number(item.remaining_minutes) > 0 && `${toPersianDigits(Number(item.remaining_minutes))} دقیقه`}
                                </>
                              )}
                            </span>
                          </div>
                        ) : (
                          <button
                            className={cn(
                              "flex h-8 min-w-[60px] items-center justify-center gap-1 rounded-xl px-3 text-xs font-bold transition-all",
                              canUse
                                ? `${actionButtonStyle} text-white active:scale-95`
                                : "bg-slate-400 text-white cursor-not-allowed",
                              isThisPurchasing && "opacity-50 pointer-events-none"
                            )}
                            disabled={isDisabled || isThisPurchasing}
                            onClick={() => canUse && handlePurchase(item)}
                          >
                            {isThisPurchasing ? (
                              <span className="animate-pulse">...</span>
                            ) : (
                              <>
                                <span className="pt-0.5">{toPersianDigits(item.gems_price)}</span>
                                <GemIcon
                                  size={14}
                                  color={canUse ? 'white' : 'flatWhite'}
                                  className="-translate-y-px"
                                />
                              </>
                            )}
                          </button>
                        )}
                      </div>

                      <div className="-mt-2 flex items-start gap-3">
                        {/* Icon with active badge */}
                        <div className="relative flex-shrink-0">
                          <div className={cn("flex size-11 items-center justify-center rounded-xl", iconStyle.bg, iconStyle.color)}>
                            <GemItemIcon icon={item.icon} action={item.action} className="w-5 h-5" />
                          </div>
                          {isActive && (
                            <span className="absolute -top-1.5 -right-1.5 text-[8px] px-1.5 py-0.5 rounded-full bg-green-500 text-white font-bold shadow-sm">
                              فعال
                            </span>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold leading-tight text-gray-800">
                            {toPersianDigits(item.name)}
                          </p>
                          <p className="mt-1 text-xs font-medium leading-relaxed text-gray-400">
                            {toPersianDigits(item.description)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {showDiscountCode && (
                      <div className="mx-auto w-full max-w-[280px]">
                        <div className="flex items-center justify-between gap-2 rounded-2xl border border-pink-100 bg-gradient-to-l from-pink-50 via-white to-primary-50 px-3 py-2.5">
                          <div className="min-w-0 flex-1 text-center">
                            <p className="text-[10px] font-bold text-pink-600">کد تخفیف فعال</p>
                            <p className="mt-1 text-sm font-black tracking-[0.28em] text-gray-800">
                              {item.discount_code}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleCopyItemDiscountCode(item.discount_code!)}
                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white text-pink-600 shadow-sm ring-1 ring-pink-100 transition-all hover:bg-pink-50 active:scale-95"
                            aria-label="کپی کد تخفیف"
                          >
                            {copiedItemCode === item.discount_code ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Progress bar */}
                    <div className="relative mx-3 h-7 rounded-full bg-gray-100 overflow-hidden">
                      <motion.div
                        initial={{ width: shouldAnimate ? 0 : `${item.progress}%` }}
                        animate={{ width: `${item.progress}%` }}
                        transition={shouldAnimate ? { duration: 1, delay: 0.5 } : { duration: 0 }}
                        className={cn(
                          "absolute right-0 top-0 h-full rounded-full flex items-center justify-end px-2",
                          progressBarStyle
                        )}
                      >
                        {gemsHave > 0 && (
                          <span className="text-[10px] font-bold text-white drop-shadow-sm">
                            {toPersianDigits(gemsHave)}
                          </span>
                        )}
                      </motion.div>

                      {/* Total gems price - white when progress is full */}
                      <div className="absolute left-2 top-0 h-full flex items-center">
                        <span className={cn(
                          "text-[10px] font-bold transition-colors",
                          isProgressFull ? "text-white drop-shadow-sm" : "text-gray-500"
                        )}>
                          {toPersianDigits(item.gems_price)}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>

      </div>
    </div>
  );
};

export default GemsPage;
